import { getHomePageDataByUrl } from '@/api/content';
import Swiper, { SwiperSlide } from '@/components/common/Swiper';
import useTranslation from 'next-translate/useTranslation';
import { FC, useEffect, useState } from 'react';

import { twMerge } from 'tailwind-merge';
import ImpTracker from '@/components/common/ImpTracker';
import useImpressionT2 from '@/tracks/useImpressionT2';
import useClickActionT2 from '@/tracks/useClickActionT2';
import styles from './trending.module.scss';
import { CroppedImage } from '@weee-fe/module';
import { useRouter } from 'next/router';
import { SubCompProps } from '../../component/LayoutLar';

const SubTrending: FC<SubCompProps> = props => {
  const { postUrl, onFetch, cmsComponentKey, properties, componentPos, subComponentPos, layoutKey } = props;
  const { lang } = useTranslation('common');
  const [load, setLoad] = useState(true);
  const { bannerImpression } = useImpressionT2();
  const { bannerClick } = useClickActionT2();
  const router = useRouter();

  const [data, setData] = useState<
    {
      hots: {
        jump_url: string;
        img_url: string;
        keyword: string;
      }[];
      key: string;
    }[]
  >([]);
  const eventKey = properties?.event_key || cmsComponentKey;

  const fetchData = async () => {
    try {
      const { result, object } = await getHomePageDataByUrl(postUrl, {
        lang,
        dataobject_key: props?.componentDataSourceKey
      });
      if (result && Array.isArray(object) && object.length > 0) {
        setLoad(false);
        setData(object);
        onFetch('done');
      } else {
        onFetch('error');
      }
    } catch (error) {
      onFetch('error');
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleNavigate = link => {
    router.push(link);
  };

  if (load) {
    return <SubTrendingSkeleton />;
  }

  return (
    <div className="rounded-300">
      {data ? (
        <div className="w-full h-full border border-solid border-surface-100-hairline rounded-300">
          <Swiper
            spaceBetween={20}
            pagination={{
              dynamicBullets: true,
              dynamicMainBullets: 3
            }}
            loop
            autoplay={{
              delay: 10000,
              disableOnInteraction: false
            }}
            slidesPerView={1}
            className={twMerge('rounded-400 h-full', styles.swiper)}>
            {data?.map((item, i) => {
              return (
                <SwiperSlide key={`banner-${i}`}>
                  <ImpTracker
                    impTrackingT2={() => {
                      if (item) {
                        bannerImpression({
                          mod_nm: layoutKey,
                          mod_pos: componentPos,
                          sec_nm: eventKey,
                          sec_pos: subComponentPos,
                          co: {
                            banner_pos: i,
                            banner_key: item.key,
                            banner_type: 'hot keyword'
                          },
                          ctx: {}
                        });
                      }
                    }}
                    className="h-full">
                    <div className="box-border flex items-center justify-center h-[37px] enki-body-base-strong text-surface-100-fg-minor border-b border-solid border-surface-100-hairline">
                      {item.key}
                    </div>
                    {item.hots?.map((subItem, subIndex) => {
                      return (
                        <div
                          className="px-100"
                          key={subItem.keyword}
                          onClick={() => {
                            if (subItem.jump_url) {
                              bannerClick({
                                mod_nm: layoutKey,
                                mod_pos: componentPos,
                                sec_nm: eventKey,
                                sec_pos: subComponentPos,
                                co: {
                                  click_type: 'view',
                                  target_type: 'hot keyword',
                                  target_nm: subItem.keyword
                                },
                                ctx: { tag_id: item.key }
                              });
                            }
                            handleNavigate(subItem.jump_url);
                          }}>
                          <div
                            className={`px-1 flex items-center h-[37px] enki-body-base-medium text-surface-100-fg-default ${
                              subIndex !== item.hots?.length - 1 && 'border-b border-solid border-surface-100-hairline'
                            }`}>
                            <CroppedImage
                              url={subItem.img_url}
                              configs={{ renderWidth: 24, renderHeight: 24 }}
                              type="img"
                              alt={`weee_${subItem?.keyword}`}
                              wrapperClassName="w-6 h-6 mr-2.5"
                            />
                            <div className="text-[13px] text-surface-100-fg-default truncate">{subItem.keyword}</div>
                          </div>
                        </div>
                      );
                    })}
                  </ImpTracker>
                </SwiperSlide>
              );
            })}
          </Swiper>
        </div>
      ) : (
        <SubTrendingSkeleton />
      )}
    </div>
  );
};

export default SubTrending;

export const SubTrendingSkeleton: FC = () => {
  return (
    <div className="w-full h-full grid grid-rows-6 gap-4">
      <div className="bg-surface-200-bg skeleton" />
      <div className="bg-surface-200-bg skeleton" />
      <div className="bg-surface-200-bg skeleton" />
      <div className="bg-surface-200-bg skeleton" />
      <div className="bg-surface-200-bg skeleton" />
      <div className="bg-surface-200-bg skeleton" />
    </div>
  );
};
